/**
 * SecurityOps — the operational picture for security staff.
 *
 * Everything here is scoped by the backend to the campuses, churches and
 * events the signed-in user is allowed to see. Camera figures are what the
 * register was last *told* by the monitoring integration — this screen never
 * dresses that up as a live hardware check.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertTriangle, BedDouble, CalendarClock, Camera, ClipboardList, Loader2,
  ScanLine, ShieldAlert, ShieldCheck, UserCheck, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Request failed');
  return res.json();
};

const Stat = ({ icon: Icon, label, value, tone = 'slate', hint, testId }) => (
  <div className={`rounded-lg border border-${tone}-200 bg-${tone}-50/60 p-4`} data-testid={testId}>
    <div className="flex items-center gap-2 text-slate-600 text-xs font-medium uppercase tracking-wide">
      <Icon className="h-3.5 w-3.5" />{label}
    </div>
    <div className="text-2xl font-semibold text-slate-900 mt-1">{value}</div>
    {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
  </div>
);

const VERDICTS = {
  authorised_resident: { label: 'Authorised resident', cls: 'bg-emerald-100 text-emerald-800', Icon: ShieldCheck },
  visitor_on_site: { label: 'Visitor, signed in', cls: 'bg-sky-100 text-sky-800', Icon: UserCheck },
  visitor_expected: { label: 'Visitor expected', cls: 'bg-sky-100 text-sky-800', Icon: UserCheck },
  check_with_supervisor: { label: 'Check with supervisor', cls: 'bg-amber-100 text-amber-900', Icon: ShieldAlert },
  not_recognised: { label: 'Not recognised', cls: 'bg-slate-200 text-slate-700', Icon: AlertTriangle },
};

const VerifyPanel = () => {
  const [form, setForm] = useState({ person_id: '', pass_code: '', phone: '', name: '' });
  const [result, setResult] = useState(null);

  const check = useMutation({
    mutationFn: () => api('/api/admin/security/verify-person', {
      method: 'POST', body: JSON.stringify(form),
    }),
    onSuccess: setResult,
    onError: (e) => toast.error(e.message),
  });

  const verdict = result ? (VERDICTS[result.verdict] || VERDICTS.not_recognised) : null;

  return (
    <Card data-testid="security-verify-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ScanLine className="h-4 w-4 text-indigo-600" /> Check a person at the gate
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          {[['pass_code', 'Pass code / badge'], ['person_id', 'Profile id from a scan'],
            ['phone', 'Phone'], ['name', 'Full name']].map(([key, label]) => (
            <Input key={key} placeholder={label} value={form[key]}
                   onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                   data-testid={`security-verify-${key}`} />
          ))}
        </div>
        <Button onClick={() => check.mutate()} disabled={check.isPending}
                data-testid="security-verify-submit">
          {check.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Check
        </Button>

        {result && (
          <div className="rounded-lg border border-slate-200 p-4 space-y-3"
               data-testid="security-verify-result">
            <div className="flex items-center gap-2">
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${verdict.cls}`}
                    data-testid="security-verify-verdict">
                {verdict.label}
              </span>
              <span className="text-xs text-slate-500">{result.because}</span>
            </div>

            {result.where_they_may_go && (
              <div className="text-sm text-slate-700" data-testid="security-verify-where">
                <BedDouble className="h-3.5 w-3.5 inline mr-1 text-slate-400" />
                {result.where_they_may_go.building} · room {result.where_they_may_go.room}
                <span className="text-slate-500"> · until {result.where_they_may_go.until}</span>
              </div>
            )}

            {result.restriction?.restricted && (
              <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-2"
                   data-testid="security-verify-restriction">
                A restriction applies ({result.restriction.entries.map(e => e.scope).join(', ')}).
                {result.restriction.detail_withheld
                  ? ' The reasons are held by the officers authorised to see them.'
                  : ''}
              </div>
            )}

            {result.visitor?.visits?.length > 0 && (
              <div className="text-xs text-slate-600">
                Last gate record: {result.visitor.visits[0].purpose || 'visit'}
                {result.visitor.visits[0].host ? ` · host ${result.visitor.visits[0].host}` : ''}
                {result.visitor.visits[0].checked_in_at
                  ? ` · in ${new Date(result.visitor.visits[0].checked_in_at).toLocaleString()}` : ''}
              </div>
            )}

            <p className="text-[10px] text-slate-400">
              Not shown here by design: {result.withheld.join(', ')}.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/** Opening an incident from the dashboard and handing it to an officer.
 *
 *  A supervisor could see the list but had to leave the screen to do anything
 *  about it; this opens the record and assigns it in place (iter 703). */
const IncidentDrawer = ({ incidentId, onClose }) => {
  const qc = useQueryClient();
  const [assignee, setAssignee] = useState('');
  const [status, setStatus] = useState('');
  const [note, setNote] = useState('');

  const { data: incident, isLoading } = useQuery({
    queryKey: ['security-incident', incidentId],
    queryFn: () => api(`/api/incidents/${incidentId}`),
    enabled: !!incidentId,
  });
  const { data: officers } = useQuery({
    queryKey: ['security-officer-list'],
    queryFn: () => api('/api/admin/security-officers'),
    enabled: !!incidentId,
    staleTime: 300000,
  });

  const save = useMutation({
    mutationFn: () => {
      const officer = (officers || []).find((o) => o.email === assignee);
      return api(`/api/incidents/${incidentId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          // Both fields: the dashboard's "unassigned" count reads the id, and
          // the list shows the email (review, iter 703).
          ...(assignee ? { assigned_to: assignee,
                           assigned_to_id: String(officer?.id || officer?._id || '') } : {}),
          ...(status ? { status } : {}),
          ...(note ? { notes: note } : {}),
        }),
      });
    },
    onSuccess: () => {
      toast.success('Incident updated');
      qc.invalidateQueries({ queryKey: ['security-operations'] });
      qc.invalidateQueries({ queryKey: ['security-incident', incidentId] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open={!!incidentId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="security-incident-drawer">
        <DialogHeader>
          <DialogTitle className="text-base">
            {isLoading ? 'Opening…' : (incident?.category || '').replace(/_/g, ' ') || 'Incident'}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin mx-auto my-8" />
        ) : !incident ? (
          <p className="text-sm text-slate-500 py-6" data-testid="security-incident-denied">
            This incident is not available to you.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-slate-700 whitespace-pre-wrap">
              {incident.description || 'No description recorded.'}
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-500">
              <div>Where: {incident.location || incident.context_type || '—'}</div>
              <div>Urgency: {incident.urgency}</div>
              <div>Status: {(incident.status || '').replace(/_/g, ' ')}</div>
              <div>Reported: {incident.created_at ? new Date(incident.created_at).toLocaleString() : '—'}</div>
              <div className="col-span-2">
                Assigned to: {incident.assigned_to || 'nobody yet'}
              </div>
            </div>

            <div className="space-y-2 border-t pt-3">
              <Label className="text-xs">Hand it to an officer</Label>
              <select value={assignee} onChange={(e) => setAssignee(e.target.value)}
                      className="w-full h-9 rounded-md border border-slate-200 text-sm px-2"
                      data-testid="security-incident-assignee">
                <option value="">Leave as it is</option>
                {(officers || [])
                  .filter((o) => o.is_active !== false && o.email)
                  .map((o) => (
                  <option key={o.id || o._id} value={o.email}>
                    {o.name}{o.campus_name ? ` · ${o.campus_name}` : ''}
                    {o.badge_number ? ` · ${o.badge_number}` : ''}
                  </option>
                ))}
              </select>

              <Label className="text-xs">Move it on</Label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}
                      className="w-full h-9 rounded-md border border-slate-200 text-sm px-2"
                      data-testid="security-incident-status">
                <option value="">Leave as it is</option>
                {['submitted', 'under_review', 'action_taken', 'resolved', 'closed'].map((s) => (
                  <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                ))}
              </select>

              <Label className="text-xs">Note</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)}
                     placeholder="What you have done or found"
                     data-testid="security-incident-note" />
            </div>

            <Button onClick={() => save.mutate()}
                    disabled={save.isPending || (!assignee && !status && !note)}
                    data-testid="security-incident-save">
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function SecurityOps() {
  const [openIncident, setOpenIncident] = useState(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['security-operations'],
    queryFn: () => api('/api/admin/security/operations'),
    refetchInterval: 60000,
  });

  if (isLoading) {
    return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  }
  if (error) {
    return (
      <div className="p-8 text-center text-slate-500" data-testid="security-ops-error">
        {error.message}
      </div>
    );
  }

  const inc = data.incidents || {};
  const cam = data.cameras || {};
  const vis = data.visitors || {};

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="security-ops-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Campus security</h1>
        <p className="text-sm text-slate-500">
          {data.scope?.unrestricted
            ? 'Every campus in the ministry.'
            : 'Incidents are limited to the campuses, churches and events your role '
              + 'covers. Visitor, officer and camera figures are estate-wide counts; '
              + 'the names behind them are not shown at your level.'}
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={ShieldAlert} label="Open incidents" value={inc.unresolved ?? 0}
              hint={`${inc.by_urgency?.red ?? 0} red · ${inc.by_urgency?.amber ?? 0} amber`}
              testId="security-stat-incidents" />
        <Stat icon={ClipboardList} label="Unassigned" value={inc.needs_assignment ?? 0}
              hint="waiting for an officer" testId="security-stat-unassigned" />
        <Stat icon={Users} label="Visitors on site" value={vis.on_site ?? 0}
              hint={`${vis.overstaying_count ?? 0} over ${vis.overstay_hours}h · estate-wide`}
              testId="security-stat-visitors" />
        <Stat icon={UserCheck} label="Officers on duty" value={data.officers_on_duty_count ?? 0}
              hint="estate-wide" testId="security-stat-officers" />
      </div>

      <VerifyPanel />
      <IncidentDrawer incidentId={openIncident} onClose={() => setOpenIncident(null)} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="security-incidents-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Incidents needing attention</CardTitle>
          </CardHeader>
          <CardContent>
            {(inc.recent || []).length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">Nothing open.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {inc.recent.map((row) => (
                  <button key={row.id} type="button"
                          onClick={() => setOpenIncident(row.id)}
                          className="w-full py-2.5 flex items-center gap-3 text-left hover:bg-slate-50 transition-colors"
                          data-testid={`security-incident-${row.id}`}>
                    <span className={`h-2 w-2 rounded-full shrink-0 ${
                      row.urgency === 'red' ? 'bg-red-500'
                        : row.urgency === 'amber' ? 'bg-amber-500' : 'bg-yellow-400'}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-900 truncate">
                        {row.category.replace(/_/g, ' ')}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {row.location || row.context_type} · {row.status.replace(/_/g, ' ')}
                        {row.assigned_to ? ` · ${row.assigned_to}` : ' · unassigned'}
                      </div>
                    </div>
                    <span className="text-[11px] text-slate-400 shrink-0">
                      {row.created_at ? new Date(row.created_at).toLocaleDateString() : ''}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {!data.scope?.sees_sensitive && (
              <p className="text-[10px] text-slate-400 mt-3">
                Safeguarding and missing-person reports are not shown on this list.
              </p>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card data-testid="security-cameras-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Camera className="h-4 w-4 text-slate-500" /> Cameras
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline">{cam.registered ?? 0} registered</Badge>
                <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                  {cam.reported_online ?? 0} reported online
                </Badge>
                <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                  {cam.reported_offline ?? 0} reported offline
                </Badge>
                <Badge variant="outline">{cam.never_reported ?? 0} never reported</Badge>
                {cam.stopped_reporting > 0 && (
                  <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100">
                    {cam.stopped_reporting} went silent
                  </Badge>
                )}
                {cam.open_alerts > 0 && (
                  <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100">
                    {cam.open_alerts} open alerts
                  </Badge>
                )}
              </div>
              <p className="text-[10px] text-slate-400">{cam.source}</p>
            </CardContent>
          </Card>

          <Card data-testid="security-clearance-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarClock className="h-4 w-4 text-slate-500" /> Accommodation to clear
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(data.accommodation_clearance?.reservations || []).length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center">
                  No rooms are being cleared for an event.
                </p>
              ) : (
                <div className="divide-y divide-slate-100">
                  {data.accommodation_clearance.reservations.map((r) => (
                    <div key={r.reservation_id} className="py-2.5"
                         data-testid={`security-clearance-${r.reservation_id}`}>
                      <div className="text-sm text-slate-900">{r.event_name}</div>
                      <div className="text-[11px] text-slate-500">
                        {r.rooms} rooms · vacate by {r.vacate_by || 'tbc'}
                        {r.occupants_to_move ? ` · ${r.occupants_to_move} still to move` : ' · all clear'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {(vis.overstaying || []).length > 0 && (
        <Card data-testid="security-overstays-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Visitors still on site after {vis.overstay_hours} hours</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-slate-100">
              {vis.overstaying.map((v) => (
                <div key={v.visitor_id} className="py-2 text-sm flex justify-between gap-3">
                  <span className="text-slate-900 truncate">{v.name || 'Unnamed visitor'}</span>
                  <span className="text-[11px] text-slate-500 shrink-0">
                    since {v.since ? new Date(v.since).toLocaleString() : 'unknown'}
                    {v.host ? ` · ${v.host}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
